// RC2 Sprint 0 · Reachability — Global Inbox
// Surfaces every notification across every project the user is involved
// with. Reuses the existing GET /api/notifications endpoint (Iter 83.1)
// and ProjectInbox row component for visual consistency. NO new
// backend. NO new functionality. Reachability only.
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import {
  Inbox as InboxIcon, AtSign, MessageSquare, Package, Share2,
  GitBranch, FileText, AlertTriangle, Check, Eye,
} from "lucide-react";
import { api } from "@/lib/api";

const FILTERS = [
  { key: "all",         label: "All",         match: () => true },
  { key: "unread",      label: "Unread",      match: (n) => !n.read },
  { key: "mentions",    label: "Mentions",    match: (n) => n.notification_type === "mention" },
  { key: "assignments", label: "Assigned",    match: (n) => n.notification_type === "punch.assigned" },
  { key: "overdue",     label: "Overdue",     match: (n) =>
      ["punch.overdue", "punch.escalated", "punch.due_soon"].includes(n.notification_type) },
  { key: "punch",       label: "Punch",       match: (n) => (n.notification_type || "").startsWith("punch.") },
  { key: "plans",       label: "Plans",       match: (n) => /^(markup|object)\./.test(n.notification_type || "") },
  { key: "exports",     label: "Exports",     match: (n) => (n.notification_type || "").startsWith("export.") },
  { key: "sharing",     label: "Sharing",     match: (n) => (n.notification_type || "").startsWith("share.") },
];

const TYPE_ICON = {
  mention: AtSign,
  "punch.created": MessageSquare,
  "punch.assigned": MessageSquare,
  "punch.updated": MessageSquare,
  "punch.closed": MessageSquare,
  "punch.rejected": MessageSquare,
  "punch.ready_for_review": MessageSquare,
  "punch.due_soon": AlertTriangle,
  "punch.overdue": AlertTriangle,
  "punch.escalated": AlertTriangle,
  "export.completed": Package,
  "export.failed": Package,
  "share.created": Share2,
  "share.revoked": Share2,
  "markup.created": GitBranch,
  "object.created": FileText,
  "object.updated": FileText,
};

const PRIORITY_COLOR = {
  urgent: "text-red-500",
  high:   "text-orange-400",
  normal: "text-[var(--fop-text-2)]",
  low:    "text-[var(--fop-text-3)]",
};

export default function GlobalInbox() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 200 } });
      setItems(data.items || data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return items.filter(f.match);
  }, [items, filter]);

  const counters = useMemo(() => {
    const c = {};
    for (const f of FILTERS) c[f.key] = items.filter(f.match).length;
    return c;
  }, [items]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.patch(`/notifications/${n.id}/read`);
      setItems((curr) => curr.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    await markRead(n);
    if (n.deep_link) navigate(n.deep_link);
  };

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="global-inbox">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-center gap-3">
          <InboxIcon className="text-[var(--fop-primary)]" size={20} />
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              ForgedOps · Inbox
            </div>
            <h1 className="text-2xl font-semibold mt-1">Everything that needs you</h1>
          </div>
        </header>

        <div className="flex gap-1 mb-4 flex-wrap" data-testid="inbox-filters">
          {FILTERS.map((f) => {
            const count = counters[f.key] || 0;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`inbox-filter-${f.key}`}
                className={`text-[11px] uppercase tracking-wider font-mono
                              px-2.5 py-1 rounded transition-colors inline-flex items-center gap-1.5
                              ${filter === f.key
                                ? "bg-[var(--fop-primary)] text-black"
                                : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"}`}
              >
                {f.label}
                {count > 0 && (
                  <span
                    data-testid={`inbox-count-${f.key}`}
                    className={`text-[9px] tracking-normal font-semibold rounded-sm px-1 ${
                      filter === f.key
                        ? "bg-black text-[var(--fop-primary)]"
                        : "bg-[var(--fop-surface)] text-[var(--fop-text-2)]"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden">
          {loading && (
            <div className="px-4 py-8 text-center text-[var(--fop-text-3)] text-sm" data-testid="inbox-loading">
              Loading inbox…
            </div>
          )}
          {!loading && visible.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="inbox-empty">
              <Check size={28} className="mx-auto text-[var(--fop-text-3)] mb-2" />
              <div className="text-sm text-[var(--fop-text-2)]">
                Nothing in this view.
              </div>
              <div className="text-[11px] uppercase tracking-wider font-mono text-[var(--fop-text-3)] mt-2">
                Watch a project on the project page to start receiving updates.
              </div>
            </div>
          )}
          <ul>
            {visible.map((n) => {
              const Icon = TYPE_ICON[n.notification_type] || InboxIcon;
              return (
                <li
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`px-4 py-3 border-b border-[var(--fop-border)] last:border-b-0
                                cursor-pointer hover:bg-[var(--fop-surface-2)] transition-colors flex gap-3
                                ${!n.read ? "bg-[var(--fop-surface-2)]/40" : ""}`}
                  data-testid={`inbox-item-${n.id}`}
                >
                  <Icon size={14} className={`mt-1 shrink-0 ${PRIORITY_COLOR[n.priority] || "text-[var(--fop-text-2)]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {!n.read && (
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--fop-primary)] shrink-0" />
                      )}
                      <div className={`text-sm truncate ${n.read ? "text-[var(--fop-text-2)]" : "text-[var(--fop-text)] font-medium"}`}>
                        {n.title}
                      </div>
                    </div>
                    {n.body && (
                      <div className="text-[12px] text-[var(--fop-text-3)] line-clamp-2">
                        {n.body}
                      </div>
                    )}
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
                      {(n.notification_type || "").replace(".", " · ")}
                      {n.priority && <span className="ml-2">· {n.priority}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
